import { apiFetch } from "@core/http";
import type { Template, TemplateDraft } from "@core/types";

export type { Template, TemplateDraft } from "@core/types";

const base = "/v1/portal/templates";

// SPEC-218 §3, criterio 1: sin alta ni baja — el contrato solo publica GET,
// PUT/{code}, POST/{code}/preview y POST/{code}/test (30-prizes-redeemables.yaml).
export const listTemplates = () => apiFetch<Template[]>(base);

// El body de `PUT` (forma ya publicada por api-http): `code`, `channel` y
// `parameters` no viajan porque no son editables (criterio 3) — ni siquiera
// se declaran en el requestBody del contrato.
export interface TemplateEditInput {
  subject?: TemplateDraft["subject"];
  body: NonNullable<TemplateDraft["body"]>;
  active?: boolean;
  confirmed?: boolean;
}

export const editTemplate = (code: string, input: TemplateEditInput) =>
  apiFetch<Template>(`${base}/${encodeURIComponent(code)}`, {
    method: "PUT",
    body: input,
  });

export interface TemplatePreview {
  es: string;
  en: string;
}

// PA-036-C/PA-036-E: siempre se manda el borrador en pantalla (aunque no
// difiera de lo persistido), para que la previsualización muestre lo que el
// usuario acaba de escribir, no lo último guardado.
export const previewTemplate = (code: string, draft: TemplateDraft) =>
  apiFetch<TemplatePreview>(`${base}/${encodeURIComponent(code)}/preview`, {
    method: "POST",
    body: draft,
  });

export interface TemplateTestResult {
  maskedEmail: string;
}

export const sendTemplateTest = (code: string, draft: TemplateDraft) =>
  apiFetch<TemplateTestResult>(`${base}/${encodeURIComponent(code)}/test`, {
    method: "POST",
    body: draft,
  });

// Reconstruye el `TemplateEditInput` a partir del registro persistido, con
// overrides puntuales — mismo criterio que `buildInput(defaultsFor(offer))`
// en offers/OffersScreen.tsx para reactivar sin repetir la forma del body a
// mano. Se usa tanto para el toggle de la tabla como para "Desactivar" del
// editor: ninguno de los dos reescribe el asunto/cuerpo, solo `active`.
export function buildEditInputFromRecord(
  record: Template,
  overrides: Partial<Pick<TemplateEditInput, "active" | "confirmed">> = {},
): TemplateEditInput {
  // SPEC-218 §4: el asunto solo existe en canal correo — en push, un
  // `subject` entrante se rechaza (`INVALID_REQUEST`). El seed de desarrollo
  // igual guarda un `subject` en las plantillas push (título de la
  // notificación), así que no alcanza con mirar si el campo vino definido:
  // hay que mirar el canal real del registro.
  const includeSubject =
    record.channel === "email" && record.subject !== undefined;
  return {
    ...(includeSubject ? { subject: record.subject } : {}),
    body: record.body,
    active: record.active,
    ...overrides,
  };
}
